import { useState, useEffect, useCallback } from "react";
import { QrCode, Plus, Trash2, Printer, Loader2 } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useConfig } from "./ConfiguracoesLayout";
import ConfigSection from "./ConfigSection";
import { supabase } from "@/integrations/supabase/client";

interface RestTable { id: string; table_number: number; label: string | null; is_active: boolean }

const QrCodes = () => {
  const { restaurant, toast } = useConfig();
  const [tables, setTables] = useState<RestTable[]>([]);
  const [newTableCount, setNewTableCount] = useState("");
  const [addingTables, setAddingTables] = useState(false);

  const baseUrl = typeof window !== "undefined" ? window.location.origin : "";

  const fetchTables = useCallback(async (restId: string) => {
    const { data } = await supabase
      .from("restaurant_tables")
      .select("*")
      .eq("restaurant_id", restId)
      .order("table_number");
    setTables((data as RestTable[]) || []);
  }, []);

  useEffect(() => {
    if (restaurant) fetchTables(restaurant.id);
  }, [restaurant, fetchTables]);

  const addTables = async () => {
    if (!restaurant || !newTableCount) return;
    const count = parseInt(newTableCount);
    if (isNaN(count) || count < 1) return;
    setAddingTables(true);
    const maxNum = tables.length > 0 ? Math.max(...tables.map(t => t.table_number)) : 0;
    const rows = Array.from({ length: count }, (_, i) => ({
      restaurant_id: restaurant.id,
      table_number: maxNum + i + 1,
    }));
    const { error } = await supabase.from("restaurant_tables").insert(rows);
    if (error) {
      toast({ title: "Erro ao criar mesas", description: error.message, variant: "destructive" });
    } else {
      toast({ title: `${count} mesa(s) criada(s)!` });
      setNewTableCount("");
      fetchTables(restaurant.id);
    }
    setAddingTables(false);
  };

  const deleteTable = async (id: string) => {
    if (!restaurant) return;
    await supabase.from("restaurant_tables").delete().eq("id", id);
    fetchTables(restaurant.id);
  };

  const printQR = (elementId: string, title: string) => {
    const el = document.getElementById(elementId);
    if (!el) return;
    const svg = el.querySelector("svg");
    if (!svg) return;
    const svgData = new XMLSerializer().serializeToString(svg);
    const win = window.open("", "_blank");
    if (!win) return;
    win.document.write(`
      <html><head><title>${title}</title><style>
        body { display: flex; flex-direction: column; align-items: center; justify-content: center; min-height: 100vh; margin: 0; font-family: system-ui, sans-serif; }
        h2 { margin-bottom: 16px; font-size: 24px; } p { color: #666; font-size: 14px; margin-top: 12px; }
        @media print { button { display: none; } }
      </style></head><body>
        <h2>${title}</h2>
        ${svgData}
        <p>Escaneie o QR Code para acessar o cardápio</p>
        <button onclick="window.print()" style="margin-top:20px;padding:10px 24px;font-size:16px;cursor:pointer;border:1px solid #ccc;border-radius:8px;background:white;">Imprimir</button>
      </body></html>
    `);
    win.document.close();
  };

  if (!restaurant) return null;

  return (
    <div className="space-y-6">
      {/* Main QR Code */}
      <ConfigSection title="QR Code do Cardápio" icon={QrCode}>
        <div className="flex flex-col sm:flex-row items-start gap-5">
          <div id="qr-main" className="p-4 bg-white rounded-xl border border-border shrink-0">
            <QRCodeSVG
              value={`${baseUrl}/${restaurant.slug}`}
              size={160}
              level="H"
              includeMargin
            />
          </div>
          <div className="flex-1 space-y-3">
            <div>
              <p className="text-sm font-medium mb-1">Link do cardápio:</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-xs bg-card border border-border rounded-lg px-3 py-2 truncate">
                  {baseUrl}/{restaurant.slug}
                </code>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    navigator.clipboard.writeText(`${baseUrl}/${restaurant.slug}`);
                    toast({ title: "Link copiado!" });
                  }}
                >
                  Copiar
                </Button>
              </div>
            </div>
            <Button
              size="sm"
              variant="outline"
              className="gap-1.5"
              onClick={() => printQR("qr-main", restaurant.name)}
            >
              <Printer className="h-3.5 w-3.5" />
              Imprimir QR Code
            </Button>
          </div>
        </div>
      </ConfigSection>

      {/* Table QR Codes */}
      <ConfigSection title="Mesas & QR Codes" icon={QrCode}>
        <div className="flex items-end gap-3 mb-4">
          <div className="space-y-1 flex-1">
            <Label>Adicionar mesas</Label>
            <div className="flex gap-2">
              <Input
                type="number"
                min="1"
                max="50"
                placeholder="Quantidade"
                value={newTableCount}
                onChange={(e) => setNewTableCount(e.target.value)}
                className="h-10 bg-card border-border w-32"
              />
              <Button
                onClick={addTables}
                disabled={addingTables || !newTableCount}
                size="sm"
                className="h-10 gap-1.5"
              >
                {addingTables ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                Criar
              </Button>
            </div>
          </div>
          {tables.length > 0 && (
            <Button
              size="sm"
              variant="outline"
              className="h-10 gap-1.5"
              onClick={() => {
                const svgs = tables.map((t) => {
                  const el = document.getElementById(`qr-table-${t.table_number}`);
                  const svg = el?.querySelector("svg");
                  if (!svg) return "";
                  return `
                    <div style="page-break-inside: avoid; text-align: center; padding: 24px;">
                      <h3 style="margin-bottom: 8px;">Mesa ${t.table_number}${t.label ? ` — ${t.label}` : ""}</h3>
                      ${new XMLSerializer().serializeToString(svg)}
                      <p style="color:#666;font-size:12px;margin-top:8px;">Escaneie para fazer seu pedido</p>
                    </div>
                  `;
                }).filter(Boolean);
                const win = window.open("", "_blank");
                if (!win) return;
                win.document.write(`
                  <html><head><title>QR Codes das Mesas</title><style>
                    body { font-family: system-ui, sans-serif; margin: 0; padding: 20px; }
                    .grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 16px; }
                    @media print { button { display: none; } }
                  </style></head><body>
                    <h2 style="text-align:center;margin-bottom:24px;">${restaurant.name} — QR Codes das Mesas</h2>
                    <div class="grid">${svgs.join("")}</div>
                    <div style="text-align:center;margin-top:20px;">
                      <button onclick="window.print()" style="padding:10px 24px;font-size:16px;cursor:pointer;border:1px solid #ccc;border-radius:8px;background:white;">Imprimir Todos</button>
                    </div>
                  </body></html>
                `);
                win.document.close();
              }}
            >
              <Printer className="h-3.5 w-3.5" />
              Imprimir Todos
            </Button>
          )}
        </div>

        {tables.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground text-sm">
            Nenhuma mesa cadastrada. Adicione mesas para gerar QR Codes individuais.
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {tables.map((t) => {
              const tableUrl = `${baseUrl}/${restaurant.slug}?mesa=${t.table_number}`;
              return (
                <div
                  key={t.id}
                  className="rounded-xl border border-border bg-card p-3 flex flex-col items-center gap-2"
                >
                  <div className="flex items-center justify-between w-full">
                    <span className="text-sm font-semibold">Mesa {t.table_number}</span>
                    <button
                      onClick={() => deleteTable(t.id)}
                      className="text-muted-foreground hover:text-destructive transition-colors"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  <div id={`qr-table-${t.table_number}`} className="bg-white rounded-lg p-2">
                    <QRCodeSVG value={tableUrl} size={100} level="M" includeMargin />
                  </div>
                  <div className="flex gap-1.5 w-full">
                    <Button
                      size="sm"
                      variant="ghost"
                      className="flex-1 h-7 text-[11px] gap-1"
                      onClick={() => printQR(`qr-table-${t.table_number}`, `${restaurant.name} — Mesa ${t.table_number}`)}
                    >
                      <Printer className="h-3 w-3" />
                      Imprimir
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="flex-1 h-7 text-[11px] gap-1"
                      onClick={() => {
                        navigator.clipboard.writeText(tableUrl);
                        toast({ title: `Link da Mesa ${t.table_number} copiado!` });
                      }}
                    >
                      Copiar
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </ConfigSection>
    </div>
  );
};

export default QrCodes;
